import { parseSse, type SseEvent } from './parser';

/* Fetch-based SSE consumer for the Bearer-gated admin stream.
 *
 * Loops: open `GET {path}` with the access token + `Last-Event-ID`,
 * stream-decode the body, emit parsed events, and reconnect on drop —
 * resuming from the last id so the bus replays in-window events
 * (etap 10 retention). A 401 triggers one refresh attempt before
 * reconnecting. Aborts cleanly when the returned dispose fn is called
 * (e.g. React unmount).
 *
 * One real connection per `path`, shared + ref-counted across every caller
 * (see `subscribeStream` below) — every widget/page that wants the feed
 * multicasts off a single underlying stream instead of opening its own. */

export interface StreamOptions {
  path: string;
  getToken: () => string | null;
  onEvent: (ev: SseEvent) => void;
  /** Try to refresh the session on 401. Resolve true if a new token
      is now available. */
  onUnauthorized?: () => Promise<boolean>;
  /** Called when the session is definitively invalid (refresh keeps failing).
      The app should clear auth + redirect to /login. */
  onAuthInvalidated?: () => void;
  /** Called after each successful (re)connect. Consumers resync REST state
      here so a gap longer than the bus retention window (or a lag-forced
      reconnect) is reconciled rather than silently lost. */
  onOpen?: () => void;
  /** Reconnect backoff in ms (overridable for tests). */
  reconnectDelayMs?: number;
}

/** How many consecutive refresh failures before we treat the session as
    definitively dead. A single transient 5xx/502 during a backend deploy swap
    must NOT permanently silence the stream — only a persistent failure does. */
const MAX_REFRESH_FAILURES = 3;

interface Listener {
  onEvent: (ev: SseEvent) => void;
  onOpen?: () => void;
}

interface SharedStream {
  listeners: Set<Listener>;
  isOpen: boolean;
  stop: () => void;
}

/* Every caller of `useAdminEventStream` (the app shell, the dashboard page,
 * each self-updating dashboard widget, every list page) used to open its
 * OWN long-lived `fetch()` to `path`. A single Dashboard mount alone adds up
 * to 6 concurrent connections (shell + page + 4 widgets) — exactly a
 * browser's HTTP/1.1 six-connections-per-origin cap (Vite's dev/preview
 * server has no HTTP/2). Once those 6 slots are permanently held by streams
 * that never close on their own, EVERY subsequent request to the origin —
 * a lazy route chunk's dynamic import, that route's own REST calls, even an
 * unrelated `/v1/healthz` — queues forever with no error and no timeout,
 * which reads exactly like "the page is stuck loading". Reproduced live:
 * once the dashboard's streams settle, an in-page `fetch('/v1/healthz')`
 * timed out; navigating to any other admin page never issued a single
 * network request because the lazy chunk's own module fetch was queued
 * behind them.
 *
 * Fix: dedupe concurrent subscriptions to the same `path` into one real
 * connection, ref-counted, multicasting parsed events to every listener —
 * mirrors the in-flight-request dedup `useResource` already does for REST
 * (see shared/lib/data/useResource.ts). Auth callbacks (`getToken`,
 * `onUnauthorized`, `onAuthInvalidated`) are taken from whichever caller
 * happens to create the underlying connection — safe because every caller
 * in this app wires them to the same auth store, not because they COULD
 * differ. */
const sharedByPath = new Map<string, SharedStream>();

export function subscribeStream(opts: StreamOptions): () => void {
  const listener: Listener = { onEvent: opts.onEvent, onOpen: opts.onOpen };

  const existing = sharedByPath.get(opts.path);
  if (existing) {
    existing.listeners.add(listener);
    // Late joiner on an already-live connection: it won't see the onOpen
    // that already fired for the current session, so give it one now — the
    // whole point of onOpen is "resync REST state", and a just-mounted
    // caller needs that exactly as much as one that raced the connect.
    if (existing.isOpen) listener.onOpen?.();
    return () => releaseListener(opts.path, existing, listener);
  }

  const shared: SharedStream = {
    listeners: new Set([listener]),
    isOpen: false,
    stop: () => {},
  };
  shared.stop = runSharedStream(opts, shared);
  sharedByPath.set(opts.path, shared);
  return () => releaseListener(opts.path, shared, listener);
}

function releaseListener(path: string, shared: SharedStream, listener: Listener): void {
  shared.listeners.delete(listener);
  if (shared.listeners.size > 0) return;
  // Last listener gone — actually tear down the connection instead of
  // leaking it, and drop the registry entry so the next subscriber opens a
  // fresh one rather than joining a stopped stream.
  if (sharedByPath.get(path) === shared) sharedByPath.delete(path);
  shared.stop();
}

function runSharedStream(opts: StreamOptions, shared: SharedStream): () => void {
  const reconnectDelay = opts.reconnectDelayMs ?? 2000;
  const controller = new AbortController();
  let lastEventId: string | null = null;
  let stopped = false;
  let refreshFailures = 0;

  const sleep = (ms: number) =>
    new Promise<void>((resolve) => setTimeout(resolve, ms));

  const run = async () => {
    while (!stopped) {
      try {
        const token = opts.getToken();
        const headers: Record<string, string> = {
          accept: 'text/event-stream',
        };
        if (token) headers.authorization = `Bearer ${token}`;
        if (lastEventId) headers['last-event-id'] = lastEventId;

        const resp = await fetch(opts.path, {
          method: 'GET',
          headers,
          credentials: 'include',
          signal: controller.signal,
        });

        if (resp.status === 401 && opts.onUnauthorized) {
          const ok = await opts.onUnauthorized();
          if (ok) {
            refreshFailures = 0;
            continue; // retry immediately with the refreshed token
          }
          // Refresh failed. Don't permanently stop on a single hiccup — a
          // transient 5xx during a deploy swap resolves on the next attempt.
          // Only after repeated failure is the session truly dead.
          refreshFailures += 1;
          if (refreshFailures >= MAX_REFRESH_FAILURES) {
            opts.onAuthInvalidated?.();
            return;
          }
          await sleep(reconnectDelay);
          continue; // reconnect → re-attempts refresh
        }
        if (!resp.ok || !resp.body) {
          await sleep(reconnectDelay);
          continue;
        }

        // Connected. Reset the failure counter and let every current
        // listener resync REST state to fill any gap the replay buffer
        // couldn't cover.
        refreshFailures = 0;
        shared.isOpen = true;
        for (const l of shared.listeners) l.onOpen?.();

        const reader = resp.body.getReader();
        const decoder = new TextDecoder();
        let buffer = '';
        // Pump until the server closes the stream or we're aborted.
        for (;;) {
          const { done, value } = await reader.read();
          if (done) break;
          buffer += decoder.decode(value, { stream: true });
          const { events, rest } = parseSse(buffer);
          buffer = rest;
          for (const ev of events) {
            if (ev.id) lastEventId = ev.id;
            for (const l of shared.listeners) l.onEvent(ev);
          }
        }
      } catch {
        if (controller.signal.aborted) return;
        // Network blip — fall through to the backoff + reconnect.
      } finally {
        shared.isOpen = false;
      }
      if (stopped) return;
      await sleep(reconnectDelay);
    }
  };

  void run();

  return () => {
    stopped = true;
    controller.abort();
  };
}
